import React, { useState, useMemo } from "react";
import { Calculator, TrendingUp, Users, ArrowRight, Sparkles, DollarSign, Clock, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";

const InteractiveRoiCalculator = () => {
  const [agents, setAgents] = useState(6);
  const [monthlySalary, setMonthlySalary] = useState(3000);
  const [monthlyCalls, setMonthlyCalls] = useState(12000);
  const [currency, setCurrency] = useState("USD");

  const currencySymbol = currency === "USD" ? "$" : "₹";
  const currencyMultiplier = currency === "INR" ? 83 : 1;

  const calculations = useMemo(() => {
    const adjustedSalary = monthlySalary * currencyMultiplier;
    const annualHumanCost = agents * adjustedSalary * 12;
    const costPerCall = currency === "USD" ? 0.04 : 3.3;
    const basePlatformFee = currency === "USD" ? 3000 : 250000;
    const annualAiCost = monthlyCalls * 12 * costPerCall + basePlatformFee;
    const annualSavings = Math.max(0, annualHumanCost - annualAiCost);
    const roiPercentage = annualHumanCost > 0 ? Math.round((annualSavings / annualHumanCost) * 100) : 0;
    const monthlySavings = Math.round(annualSavings / 12);
    const hoursSaved = Math.round((monthlyCalls * 4.5) / 60);

    return {
      annualHumanCost: Math.round(annualHumanCost),
      annualAiCost: Math.round(annualAiCost),
      annualSavings: Math.round(annualSavings),
      monthlySavings,
      roiPercentage,
      hoursSaved,
    };
  }, [agents, monthlySalary, monthlyCalls, currency, currencyMultiplier]);

  const formatMoney = (val) => {
    return `${currencySymbol}${val.toLocaleString()}`;
  };

  return (
    <section className="py-24 bg-slate-50 dark:bg-slate-900/90 text-slate-900 dark:text-white relative overflow-hidden border-t border-slate-200 dark:border-slate-800" id="roi-calculator">
      {/* Background ambient lighting */}
      <div className="absolute top-1/3 -left-32 w-96 h-96 bg-primary/10 dark:bg-primary/20 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute bottom-10 -right-32 w-96 h-96 bg-emerald-500/10 dark:bg-emerald-500/15 rounded-full blur-[140px] pointer-events-none" />

      <div className="wrapper relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30">
            <Sparkles size={14} /> Interactive Cost Savings Engine
          </div>
          <h2 data-aos="fade-up" className="section-heading text-slate-900 dark:text-white">
            Calculate Your AI Automation & Voice ROI
          </h2>
          <p data-aos="fade-up" className="text-slate-600 dark:text-slate-300 text-base sm:text-lg">
            See the exact financial impact of replacing manual B2B telecalling and tier-1 support queues with autonomous PANTHM AI agents.
          </p>

          {/* Currency Toggle */}
          <div className="flex justify-center pt-2">
            <div className="inline-flex p-1 rounded-xl bg-slate-200/80 dark:bg-slate-800/80 border border-slate-300 dark:border-slate-700 shadow-inner">
              <button
                type="button"
                onClick={() => setCurrency("USD")}
                className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  currency === "USD" ? "bg-primary text-white shadow-md" : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
                }`}
              >
                USD ($)
              </button>
              <button
                type="button"
                onClick={() => setCurrency("INR")}
                className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  currency === "INR" ? "bg-primary text-white shadow-md" : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
                }`}
              >
                INR (₹)
              </button>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 items-stretch">
          {/* Controls Column */}
          <div className="lg:col-span-6 bg-white dark:bg-slate-800/60 backdrop-blur-xl p-6 sm:p-8 rounded-3xl border border-slate-200 dark:border-slate-700/80 shadow-xl dark:shadow-2xl flex flex-col justify-between space-y-8">
            <div className="space-y-6">
              {/* Slider 1: Team Size */}
              <div className="space-y-2">
                <div className="flex justify-between items-center text-sm font-semibold">
                  <span className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                    <Users size={16} className="text-primary" /> Current Human Reps / SDRs
                  </span>
                  <span className="px-3 py-1 bg-primary/10 dark:bg-primary/20 text-primary border border-primary/30 rounded-lg font-mono font-bold">
                    {agents} Reps
                  </span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="50"
                  value={agents}
                  onChange={(e) => setAgents(parseInt(e.target.value))}
                  className="w-full accent-primary h-2 bg-slate-200 dark:bg-slate-700 rounded-lg cursor-pointer"
                />
                <div className="flex justify-between text-[11px] text-slate-500 font-mono">
                  <span>1 Rep</span>
                  <span>25 Reps</span>
                  <span>50 Reps</span>
                </div>
              </div>

              {/* Slider 2: Salary */}
              <div className="space-y-2">
                <div className="flex justify-between items-center text-sm font-semibold">
                  <span className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                    <DollarSign size={16} className="text-emerald-600 dark:text-emerald-400" /> Avg. Monthly Salary / Rep
                  </span>
                  <span className="px-3 py-1 bg-emerald-500/10 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-300 border border-emerald-500/30 rounded-lg font-mono font-bold">
                    {formatMoney(monthlySalary * currencyMultiplier)} / mo
                  </span>
                </div>
                <input
                  type="range"
                  min="500"
                  max="10000"
                  step="250"
                  value={monthlySalary}
                  onChange={(e) => setMonthlySalary(parseInt(e.target.value))}
                  className="w-full accent-emerald-500 h-2 bg-slate-200 dark:bg-slate-700 rounded-lg cursor-pointer"
                />
                <div className="flex justify-between text-[11px] text-slate-500 font-mono">
                  <span>{formatMoney(500 * currencyMultiplier)}</span>
                  <span>{formatMoney(5000 * currencyMultiplier)}</span>
                  <span>{formatMoney(10000 * currencyMultiplier)}</span>
                </div>
              </div>

              {/* Slider 3: Call Volume */}
              <div className="space-y-2">
                <div className="flex justify-between items-center text-sm font-semibold">
                  <span className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                    <TrendingUp size={16} className="text-blue-600 dark:text-blue-400" /> Monthly Call / Ticket Volume
                  </span>
                  <span className="px-3 py-1 bg-blue-500/10 dark:bg-blue-500/20 text-blue-600 dark:text-blue-300 border border-blue-500/30 rounded-lg font-mono font-bold">
                    {monthlyCalls.toLocaleString()} Calls
                  </span>
                </div>
                <input
                  type="range"
                  min="1000"
                  max="100000"
                  step="1000"
                  value={monthlyCalls}
                  onChange={(e) => setMonthlyCalls(parseInt(e.target.value))}
                  className="w-full accent-blue-500 h-2 bg-slate-200 dark:bg-slate-700 rounded-lg cursor-pointer"
                />
                <div className="flex justify-between text-[11px] text-slate-500 font-mono">
                  <span>1k</span>
                  <span>50k</span>
                  <span>100k</span>
                </div>
              </div>
            </div>

            <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700/60 flex items-center justify-between text-xs text-slate-600 dark:text-slate-400 shadow-sm dark:shadow-none">
              <span className="flex items-center gap-1.5 text-slate-800 dark:text-slate-300 font-medium">
                <ShieldCheck size={16} className="text-emerald-600 dark:text-emerald-400" /> SLA Guaranteed
              </span>
              <span>Sub-350ms Voice Latency • 99.99% Uptime</span>
            </div>
          </div>

          {/* Results Summary Column */}
          <div className="lg:col-span-6 bg-slate-900 text-white p-6 sm:p-8 rounded-3xl border border-primary/30 shadow-2xl flex flex-col justify-between space-y-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
              <Calculator size={140} className="text-primary" />
            </div>

            <div className="space-y-6 relative z-10">
              <div className="flex justify-between items-start">
                <div>
                  <span className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Estimated Net Impact</span>
                  <h3 className="text-2xl font-bold text-white mt-1">Projected Annual Savings</h3>
                </div>
                <span className="px-3.5 py-1.5 rounded-full text-xs font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                  {calculations.roiPercentage}% Cost Drop
                </span>
              </div>

              {/* Big Savings Number */}
              <div className="p-6 rounded-2xl bg-slate-950/80 border border-emerald-500/30">
                <div className="text-4xl sm:text-5xl font-extrabold text-emerald-400 font-mono tracking-tight">
                  {formatMoney(calculations.annualSavings)}
                </div>
                <div className="text-xs sm:text-sm text-slate-300 mt-2 flex flex-wrap items-center gap-2">
                  <span>≈ {formatMoney(calculations.monthlySavings)} saved per month</span>
                  <span className="text-slate-600">•</span>
                  <span className="text-blue-300 flex items-center gap-1">
                    <Clock size={13} /> {calculations.hoursSaved}h/mo saved
                  </span>
                </div>
              </div>

              {/* Breakdown Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-xl bg-slate-950/60 border border-slate-800">
                  <div className="text-xs text-slate-400">Current Human Team</div>
                  <div className="text-lg font-bold text-red-400 font-mono mt-1">
                    {formatMoney(calculations.annualHumanCost)}/yr
                  </div>
                </div>
                <div className="p-4 rounded-xl bg-slate-950/60 border border-slate-800">
                  <div className="text-xs text-slate-400">PANTHM Autonomous AI</div>
                  <div className="text-lg font-bold text-emerald-400 font-mono mt-1">
                    {formatMoney(calculations.annualAiCost)}/yr
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-4 relative z-10">
              <Link
                to="/contact"
                className="w-full py-4 rounded-xl primary-btn text-white font-semibold text-sm sm:text-base flex items-center justify-center gap-2 shadow-lg shadow-primary/30"
              >
                <span>Deploy AI & Claim Your Savings</span>
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InteractiveRoiCalculator;
